import MLControls from "components/market-lease/controls/"
import ConfirmationModal from "components/modal/confirmation-modal"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
import MoveRowModal from "components/modal/move-row-modal"
import NotesControls from "components/notes/"
// Controls
import Controls from "components/rent-roll/rent-roll/controls/"
// Styles
import { OverrideIndicator_s, RentRollTable_s } from "components/rent-roll/rent-roll/rent-roll-styles"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { DateField, DropdownField } from "components/table/display"
import NotesHover from "components/table/hovers/notes-hover"
import OverridesHover from "components/table/hovers/overrides-hover"
import { DateEdit, DropdownEdit, TextEdit } from "components/table/inputs"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  focusCellByContent,
  focusCellByDataId, focusFirstCellByRowIndex, getFocusedRowIndex, getPagingInfo, pageTo,
  pageToFirst, TableAutomation, unfocusAllCells
} from "components/table/table-automation"
import { Area } from 'data/area-measures'
// Data
import automationService from "data/automation"
import carouselService from "data/carousel"
import { FreeRent } from 'data/free-rent'
import { Increase } from 'data/increase'
import { Inflation } from 'data/inflation'
import keyService, * as chord from "data/key"
import { canPaste, copy, getPasteInfo, paste } from "data/lib/copy-paste"
import { checkDateMode } from "data/lib/dates"
import { handleReferences } from "data/lib/handle-references"
import { local } from 'data/lib/local-json'
import { localizeNum } from "data/lib/numbers"
import loadingService from 'data/loading'
import { MarketLease } from 'data/market-lease'
import { Recovery } from 'data/named-recoveries'
import notificationService from "data/notification"
import { Payout } from 'data/payout'
import propertyService, { Property } from 'data/property'
import { RecoveryGroup } from 'data/recovery-groups'
import { RecoveryPool } from 'data/recovery-pools'
import { Rent } from 'data/rent'
import rentRollService, { RentRoll } from "data/rent-roll"
import { ReportTag } from 'data/report-tag'
import { TenantCapital } from 'data/tenant-capital'
import toggles from "feature-toggles"
import _ from "lodash"
import { Column } from "primereact/column"
import { ContextMenu } from "primereact/contextmenu"
import reportContextService from "data/reporting/report-context"
// PR
import { DataTable } from "primereact/datatable"
import React, { useEffect, useRef, useState } from "react"

export type ContextRow = RentRoll

type PropTypes = {
  data: RentRoll[]
  areaMeasures: Area[]
  columns: string[]
  increase: Increase[]
  inflation: Inflation[]
  marketLease: MarketLease[]
  namedRecoveries: Recovery[]
  property: Property
  freeRent: FreeRent[]
  payout: Payout[]
  recoveryGroups: RecoveryGroup[]
  recoveryPools: RecoveryPool[]
  reportTags: ReportTag[]
  rent: Rent[]
  tableId: string,
  tenantCapital: TenantCapital[]
  selectedState: [ ContextRow[], ( selected: ContextRow[] ) => void ]
  flags: { deleteFlag: number, pasteFlag: number }
}

const RentRollTable = (props: PropTypes) => {
  const [ selected, setSelected ] = props.selectedState

  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const traceMenuItem = {
    label: 'Trace', icon: 'pi pi-fw pi-search', command: e=> {
      const refreshFn = () => rentRollService.fetchRentRoll(contextRow.propertyid)
      handleReferences('rentroll', contextRow.rentrollid, {refreshFn});
    }
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', _cell: true, command: e => { if(contextRow.systeminput) return;
      copy(contextCell, contextRow)
    }},
    {label: 'Move Row', icon: 'pi pi-fw pi-sort-alt', command: e => {
      automationService.broadcast("MODAL_OPENING", {})
      setShowMove(true)
    }},
    {label: 'Duplicate Row', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      const pageinfo = getPagingInfo(props.tableId)
      loadingService.toggleLoading(true, 'copy')

      const rentroll = await rentRollService.copy(contextRow.propertyid, contextRow.rentrollid)
      if (!_.isEmpty(selected)) setSelected(selected.concat(rentroll as ContextRow))

      setTimeout(function reselect(retries=10) {
        if (!retries) {
          loadingService.toggleLoading(false, 'copy')
          setSelected([])
          return
        }
        pageTo(props.tableId, pageinfo.currentPage)
        setTimeout(() => {
          const t = setTimeout(() => reselect(retries-1), 100)
          if (focusCellByDataId(props.tableId, contextRow.rentrollid)) {
            clearTimeout(t)
            loadingService.toggleLoading(false, 'copy')
          }
        }, 300)
      }, 100)
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        const rentrolls = await rentRollService.copies(contextRow.propertyid, contextRow.rentrollid, n)
        !_.isEmpty(selected) && setSelected(selected.concat(rentrolls as ContextRow[]))
      })
    }},
    {label: selected.length ? 'Delete Rows' : 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => { if(contextRow.systeminput) return;
      if (_.some(selected, 'parentrentrollid')){
        notificationService.notify({
          type: "warn",
          message: "Option records can not be deleted via multisect.",
          dismiss: 3000
        })
        return
      }
      const pager         = getPagingInfo(props.tableId);
      const currentRowNum = getFocusedRowIndex() + 1
      const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
      showDeleteConfirmation(contextRow, currentRecordIndex + 1)
    }},
    traceMenuItem
  ]

  const areaMeasures:object[]    = props.areaMeasures
  const increase:object[]        = props.increase
  const inflation:object[]       = props.inflation
  const namedRecoveries:object[] = props.namedRecoveries
  const payout:object[]          = props.payout
  const property:object          = props.property
  const freeRent:object[]        = props.freeRent
  const recoveryGroups:object[]  = props.recoveryGroups
  const recoveryPools: object[]  = props.recoveryPools
  const rent:object[]            = props.rent
  const reportTags:object[]      = props.reportTags
  const tenantCapital:object[]   = props.tenantCapital
  const ti:object[]              = _.cloneDeep(_.filter(props.tenantCapital, (v) => v.type === "ti"))
  const marketLeaseOptions:[{label: string, value: number}] = _.map(props.marketLease, (ml) => ({label: ml.code, value: ml.marketleaseid}))
  const renewalTypeOptions:string[] = ["Weighted", "Option", "Renew", "Vacate", "None"]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({} as RentRoll)
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [optionDeleteConfirmation, setOptionDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const [showReorderConfirmModal, setShowReorderConfirmModal] = useState<boolean>(false);
  const [sortPayload, setSortPayload] = useState<{organizationId:number, propertyId:number, sortBy:string, sortOrder:string}|null>(null)
  const [showMove, setShowMove] = useState(false)
  const [showColumnToggle, setShowColumnToggle] = useState<boolean>(false)
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field('rentroll')

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const rentRollSubscription = rentRollService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "RENT_ROLL_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0, props.data)
          }, 500)
          break
        case e.type === "RENT_ROLL_COPIED":
          setTimeout(() => {
            focusCellByContent(props.tableId, e.payload.code, props.data)
          }, 500)
          break
        case e.type === "SYNC_OPTIONS":
          setTimeout(() => {
            // focusCellByDataId(props.tableId, e.payload.rentrollid)
          }, 500)
          break
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            rentRollService.create(propertyService.getStoredPropertyId())
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[currentRecordIndex];
            if (!currentRow.systeminput && !currentRow.parentrentrollid) showDeleteConfirmation(currentRow, currentRecordIndex + 1)
          }
          break
      }
    })

    document.addEventListener("click", clickHandler)

    return function cleanup() {
      carouselSubscription.unsubscribe()
      rentRollSubscription.unsubscribe()
      keySubscription.unsubscribe()
      document.removeEventListener("click", clickHandler)
    }
  }, [])

  //trigger deploy diff
  function clickHandler(e){
    if (e.target.classList.contains('p-column-title')){
      // Check if sortable column
      let sortColumn;
      if (e.target.firstChild.textContent === 'Suite') sortColumn = 'Suite'
      else if (e.target.firstChild.textContent === 'Name') sortColumn = 'Name'
      else if (e.target.firstChild.textContent === 'Area') sortColumn = 'Area'
      else sortColumn = null;
      // If it's a sortable column, find click position to determine sort asc/desc
      if (sortColumn){
        const headerCell = e.target.getBoundingClientRect();
        let clickOffsetTop = e.clientY - headerCell.top;
        let clickOffsetLeft = e.clientX - headerCell.left;
        const leftOffsets = {
          'Suite': 34,
          'Name': 38,
          'Area': 30
        }
        // If clicked on icons, sort column
        const payload = {
          organizationId: _.get(local("auth.profile"), 'organizationId'),
          propertyId: local("property"),
          sortBy: sortColumn,
          sortOrder: ''
        }

        if (clickOffsetTop >= 0 && clickOffsetTop <= 22 && clickOffsetLeft >= leftOffsets[sortColumn] && clickOffsetLeft <= leftOffsets[sortColumn] + 20){
          if (clickOffsetTop < 10) payload.sortOrder = 'Ascending'
          else payload.sortOrder = 'Descending'
        }

        setSortPayload(payload);
        setShowReorderConfirmModal(true);
      }
    }
  }

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    setMenu(nonSystemMenu)
  }, [contextRow])

  const persist = (value, field, rowData, force=false) => {
    const rentroll = _.omit(_.cloneDeep(rowData), 'index') // strip fields added for table view
    _.set(rentroll, field, value)
    if (field.endsWith('date')){
      const dateField = field.substr(0, field.indexOf('.'))
      const dateMode = checkDateMode(value)
      if (dateMode) _.set(rentroll, `${dateField}.dateMode`, dateMode)
      if(dateField === 'availabledate' && !dateMode) _.set(rentroll, `${dateField}.dateMode`, "absolute")
    }
    if (!force && field == 'renewaltype' && value != 'Option' && _.get(rowData, field) == 'Option'){
      showOptionDeleteConfirmation(() => {
        persist(value, field, rowData, true)
        setOptionDeleteConfirmation(null)
      })
      return
    }
    rentRollService.patch(rentroll);
    carouselService.updateRowData(rentroll);
  }

  const refresh = async () => {
    const { propertyid } = contextRow
    const rentrolls = await rentRollService.fetchRentRoll( propertyid )
    setSelected( _.intersectionBy( rentrolls, selected, 'rentrollid' ) )
  }

  useEffect(() => {
    if ( canPaste( contextCell, selected ) ) {
      nonSystemMenu.splice( 1, 0, {
        label: selected.length ? 'Paste to Rows' : 'Paste Cell', icon: 'pi pi-fw pi-file', _cell: true, command: e => {
          if (selected.length > 1 ) {
            showPasteConfirmation( contextRow, -1 )
            return
          }
        paste(contextCell, contextRow, rentRollService.fetchRentRoll, 'rentroll', selected)
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu([traceMenuItem]) : setMenu(nonSystemMenu)
  }, [contextCell, selected.length])

  useEffect( () => {
    if ( !props.flags.deleteFlag ) return //skip first time
    showDeleteConfirmation(_.first(selected), -1 )
  }, [ props.flags.deleteFlag ] )

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      message: `Would you like to delete the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        if (selected.length > 1 ) {
          const propertyid = _.first(selected).propertyid
          rentRollService.deleteMultiple(propertyid, _.map(selected, 'rentrollid'))
        } else {
          rentRollService.deleteOne(record.propertyid, record.rentrollid)
        }
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0, props.data)
        }, 200)
      },
      rowNum: currentRow,
      code: record.suite,
      selected: _.map(selected, s => ({...s, code: s.suite}))
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( props.tableId, contextRow.rentrollid, props.data)}}
        copy={copy}
        show={true}
        title={contextRow.suite}
      />
    )
  }

  const showOptionDeleteConfirmation = (proceed) => {
    automationService.broadcast("MODAL_OPENING", {})
    setOptionDeleteConfirmation(
      <ConfirmationModal
        show={true}
        title={'Confirm Option Delete'}
        confirmText="Delete Options"
        cancelText="Cancel"
        message={`Changing the renewal type will delete all related option records below it.`}
        confirmFunc={() => {
          setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
          proceed()
        }}
        cancelFunc={() => {
          setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
          setOptionDeleteConfirmation(null)
        }}
        toggleShow={() => {
          setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
          setOptionDeleteConfirmation(null)
        }}
        confirmOnly={false}
        closeOnly={false}
      />
    )
  }

  useEffect( () => {
    if ( !props.flags.pasteFlag ) return //skip first time
    if ( !canPaste( contextCell, selected ) ) return
    showPasteConfirmation( contextRow, -1 )
  }, [ props.flags.pasteFlag ] )

  const showPasteConfirmation = ( record: ContextRow, currentRow: number ) => {
    automationService.broadcast( "MODAL_OPENING", {} )
    setDeleteConfirmation( Controls.DeleteConfirmation( {
      show: true,
      title: 'Paste',
      message: `Would you like to paste ${getPasteInfo()._meta.field} data to the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation( null )
        setTimeout( () => automationService.broadcast( "MODAL_CLOSING", {} ), 200 )
      },
      confirm: () => {
        paste( contextCell, contextRow, refresh, 'rentroll', selected )
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0, props.data)
        }, 200)
      },
      rowNum: currentRow,
      code: record.suite,
      selected: _.map(selected, s => ({...s, code: s.suite}))
    }))
  }

  function conductSort(){
    if (sortPayload){
      rentRollService.sort(sortPayload)
    } else {
      return props.data
    }
    setShowReorderConfirmModal(false)
  }

  function updateContextMenu(originalEvent){
    _.invoke(menuRef.current, "show", originalEvent)
  }

  const rowClassName = (rowData) => {
    const recordId = `record-id-${rowData.rentrollid}`
    return {
      'option-record': !!rowData['parentrentrollid'],
      [recordId]: true,
    }
  }

  const statusOptions = ['Contractual', 'Speculative', 'Mo-To-Mo'].map(value => { return { label: value, value }})
  let   typeOptions   = _.map(_.get(props, 'property.leasetypedetail'), l => { return { label: l.name, value: l.name }})

  const data = props.data;

  const filteredContextMenu = !contextRow.parentrentrollid ? menu : _.filter(menu, {_cell: true})

  return (
    <div className="rent-roll-table-container">
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={filteredContextMenu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      <MoveRowModal show={showMove} rowNum={contextRow.sortorder} close={() => setShowMove(false)} move={(kind, value) => {
        if (!_.isFinite(value)) return setShowMove(false)
        const {rentrollid, sortorder} = contextRow
        const maxRows = _.size(props.data)
        const destination = {
          'down': Math.min(sortorder + 1, maxRows),
          'up': Math.max(sortorder - 1, 1),
          'to': Math.min(maxRows, Math.max(value, 1))
        }[kind] - 1

        rentRollService.move(rentrollid, destination).then(() => setShowMove(false))
      }}/>
      <RentRollTable_s id={`${props.tableId}-scrollable-container`}>
        { data && <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth={ toggles.multiselect() ? "400px" : "355px" }
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setSelected([])
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
          selection={selected} onSelectionChange={(e) => {
            const selected = _.filter(e.value, (it) => it.systeminput !== "systemImmutable")
            setSelected(selected)
          }}
        >
          { toggles.multiselect() && <Column selectionMode="multiple" frozen={ true } style={ { minWidth: "45px", paddingLeft: "8px" } } /> }
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => rowData.parentrentrollid ? '\u21AA' : props.rowIndex + 1}
          />
          <Column style={{minWidth: "110px", maxWidth: "110px"}} className="p-editable-column suite"
            frozen={true}
            header="Suite"
            {...field('suite')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "200px", maxWidth: "200px"}} className="p-editable-column name"
            frozen={true}
            header="Name"
            {...field('tenant')}
            editor={props => TextEdit({persist, ...props})}
          />
          {props['columns'].includes('Status') && <Column style={{minWidth: "140px", maxWidth: "140px"}} className="p-editable-column"
            header="Status"
            {...field('status', (rowData, props) => DropdownField({options: statusOptions, rowData, persist, ...props}))}
            editor={props => <DropdownEdit options={statusOptions} persist={persist} {...props} />}
          />}
          {props['columns'].includes('Type') && <Column style={{minWidth: "110px", maxWidth: "110px"}} className="p-editable-column"
            header="Type"
            {...field('type', (rowData, props) => DropdownField({options: typeOptions, rowData, persist, ...props}))}
            editor={props => <DropdownEdit options={typeOptions} persist={persist} {...props} />}
          />}
          {props['columns'].includes('Start Date') && <Column {...field("begindate.date", (rowData, props) => DateField({...props, 'allowRelative': true}))} header="Begin Date" style={{minWidth: "175px", maxWidth: "175px"}}
            editor={props => DateEdit({persist, ...props, 'allowRelative': true, 'disabled': !!props.rowData.parentrentrollid})} />}
          {props['columns'].includes('End Date') && <Column {...field("enddate.date", (rowData, props) => DateField({...props, 'allowRelative': true}))} header="End Date" style={{minWidth: "175px", maxwidth: "175px"}}
            editor={props => DateEdit({persist, ...props, 'allowRelative': true, 'endOfMonth': true})} />}
          {props['columns'].includes('Area') && <Column style={{minWidth: "120px", maxWidth: "120px"}} className="p-editable-column area"
            header="Area"
            {...field('areadetail', (rowData, props) => { return(
              <CarouselTrigger field="areadetail" rowData={rowData} title="Area"
                persist={persist}
                controls={{
                  basic: {control: Controls.AreaBasic, name: 'Basic'},
                  detailed: {control: Controls.AreaDetailed, name: 'Detailed'}
                }}
                componentAtRest={()=>{
                  if (!rowData['areadetail'] || rowData['areadetail']?.['state'] === 'basic') { return rowData['areadetail']?.['area'] ? `${localizeNum(rowData['areadetail']['area'])}` : '-' }
                  if (rowData['areadetail']?.['state'] === 'detailed') { return 'Detailed' }
                }}
                hover={{
                  basic: [{key: ['areadetail', 'area'], label: 'Area'}],
                  detailed: {multiple: [{key: ['areadetail', 'date'], label: 'Date'}, {key: ['areadetail', 'area'], label: 'Area'}]},
                }}
              />)}
            )}
          />}
          {props['columns'].includes('Rent') && <Column style={{minWidth: "180px", maxWidth: "180px"}} className="p-editable-column rent"
            header="Rent"
            {...field('rentdetail', (rowData, props) => { return(
              <CarouselTrigger field="rentdetail" rowData={rowData} title="Rent"
                persist={persist}
                controls={{
                  basic: {control: Controls.RentBasic, name: 'Basic'},
                  namedItem: {control: Controls.RentNamedItem, name: 'Named Item'},
                  detailed: {control: Controls.RentDetailed, name: 'Detailed'}
                }}
                namedItems={{
                  increase,
                  rent
                }}
                defaults={{...property['defaultsrentroll']}}
                componentAtRest={() => {
                  if(!rowData['rentdetail'] || rowData['rentdetail']?.['state'] === 'basic') { return rowData['rentdetail'] ? `${localizeNum(rowData['rentdetail']?.['amt'], 4, false)}${rowData['rentdetail']?.['amtType']} | ${_.find(increase, {increaseid: rowData['rentdetail']['increaseNamedItemId']})?.['code']}` : '-' }
                  if(rowData['rentdetail']?.['state'] === 'detailed') { return 'Detailed' }
                  if(rowData['rentdetail']?.['state'] === 'namedItem') { return `${_.find(rent, {rentid: rowData['rentdetail']['rentNamedItemId']})?.['code']} | ${_.find(increase, {increaseid: rowData['rentdetail']['increaseNamedItemId']})?.['code']}` }
                }}
                description={`${_.get(rowData, "tenant")}`}
                hover={{
                  basic: [
                    {key: ['rentdetail', 'amt'], label: 'Amt'},
                    {key: ['rentdetail', 'amtType'], label: 'Amt Type'},
                    {key: ['rentdetail', 'increaseNamedItemId'], label: 'Increase'},
                  ],
                  namedItem: [{key: ['rentdetail', 'rentNamedItemId'], label: 'Rent'},{key: ['rentdetail', 'increaseNamedItemId'], label: 'Increase'}],
                  detailed: {multiple: [{key: ['rentdetail', 'date'], label: 'Date '}, {key: ['rentdetail', 'amt'], label: 'Amt'}, {key: ['rentdetail', 'amtType'], label: 'Amt Type'}]}
                }}
              />)}
            )}
          />}
          {props['columns'].includes('Free Rent') && <Column style={{minWidth: "180px", maxWidth: "180px"}} className="p-editable-column"
            header="Free Rent"
            {...field('freerentdetail', (rowData, props) => { return(
              <CarouselTrigger field="freerentdetail" rowData={rowData} title="Free Rent"
                persist={persist}
                controls={{
                  basic: {control: Controls.FreeRentBasic, name: 'Basic'},
                  detailed: {control: Controls.FreeRentDetailed, name: 'Detailed'},
                  namedItem: {control: Controls.FreeRentNamedItem, name: 'Named Item'}
                }}
                namedItems={{freeRent}}
                defaults={{...property['defaultsrentroll']}}
                componentAtRest={() => {
                  if(!rowData['freerentdetail'] || rowData['freerentdetail']?.['state'] === 'basic') { return rowData['freerentdetail'] ? `${rowData['freerentdetail']?.['amt'] ? localizeNum(rowData['freerentdetail']?.['amt'], 2, false) : ''} ${rowData['freerentdetail']?.['amtType']} ${rowData['freerentdetail']?.['revenue'] ? rowData['freerentdetail']?.['revenue'] : ''}` : '-' }
                  if(rowData['freerentdetail']?.['state'] === 'detailed') { return 'Detailed' }
                  if(rowData['freerentdetail']?.['state'] === 'namedItem') { return `${_.find(freeRent, {freerentid: rowData['freerentdetail']['freeRentNamedItemId']})?.['code']}` }
                }}
                description={`${_.get(rowData, "tenant")}`}
                hover={{
                  basic: [
                    {key: ['freerentdetail', 'amt'], label: 'Amt'},
                    {key: ['freerentdetail', 'amtType'], label: 'Amt Type'},
                    {key: ['freerentdetail', 'revenue'], label: 'Revenue'},
                  ],
                  detailed: {single: [{key: ['freerentdetail', 'amtType'], label: 'Amount Type'}, {key: ['freerentdetail', 'revenue'], label: 'Revenue'}], multiple: [{key: ['freerentdetail', 'date'], label: 'Date'}, {key: ['freerentdetail', 'amt'], label: 'Amt'}]},
                  namedItem: [{key: ['freerentdetail', 'freeRentNamedItemId'], label: 'Named Free Rent'}],
                }}
              />)}
            )}
          />}
          {props['columns'].includes('Recovery') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Recovery"
            {...field('recoverydetail', (rowData, props) => {
              const count = `Recovery Count:
              ${(props.rowData['recoverydetail']?.['namedRecoveryItems']?.length || 0)
              + (props.rowData['recoverydetail']?.['namedRecoveryGroupItems']?.length || 0)
              + (props.rowData['recoverydetail']?.['prorataRecoveryItems']?.length || 0)
              + (props.rowData['recoverydetail']?.['amtItems']?.length || 0)
              + (props.rowData['recoverydetail']?.['detailedAmtItems']?.length || 0)
              + (props.rowData['recoverydetail']?.['tenantMgmtFeeItems']?.length || 0)
              }`
              return(
              <CarouselTrigger field="recoverydetail" rowData={rowData} title={count}
                persist={persist}
                controls={{
                  namedRecoveryItems: {control: MLControls.RecoveriesNamed, name: 'Named Recoveries'},
                  namedRecoveryGroupItems: {control: MLControls.RecoveryGroups, name: 'Recovery Groups'},
                  prorataRecoveryItems: {control: MLControls.RecoveriesProRata, name: 'Pro Rata'},
                  amtItems: {control: MLControls.RecoveriesAmt, name: 'Amt'},
                  detailedAmtItems: {control: MLControls.RecoveriesDetailedAmt, name: 'Detailed Amt'},
                  tenantMgmtFeeItems: {control: MLControls.RecoveriesTenantMgmtFee, name: 'Tenant Mgmt Fee'}
                }}
                namedItems={{areaMeasures, increase, inflation, payout, tenantCapital: ti, namedRecoveries, recoveryGroups, recoveryPools, reportTags}}
                defaults={{...property['defaultsmarketlease']}}
                componentAtRest={()=>{
                  return count
                }}
                hover={{recoverydetail: props.rowData}}
                table={"rentroll"}
              />)}
            )}
          />}
          {props['columns'].includes('Misc Revenue') && <Column style={{minWidth: "160px", maxWidth: "160px"}} className="p-editable-column"
            header="Misc Revenue"
            {...field('miscrevenuedetail', (rowData, props) => { return(
              <CarouselTrigger field="miscrevenuedetail" rowData={rowData} title="Misc Revenue"
                persist={persist}
                controls={{
                  basic: {control: Controls.MiscRevenueBasic, name: 'Basic'},
                  detailed: {control: Controls.MiscRevenueDetailed, name: 'Detailed'}
                }}
                namedItems={{inflation}}
                defaults={{...property['defaultsrentroll']}}
                componentAtRest={() => {
                  if(!rowData['miscrevenuedetail'] || rowData['miscrevenuedetail']?.['state'] === 'basic') { return rowData['miscrevenuedetail'] ? `${rowData['miscrevenuedetail']?.['amt'] ? `${localizeNum(rowData['miscrevenuedetail']?.['amt'], 2, false)}${rowData['miscrevenuedetail']['amtType']}` : ''}` : '-' }
                  if(rowData['miscrevenuedetail']?.['state'] === 'detailed') { return 'Detailed' }
                }}
                description={`${_.get(rowData, "tenant")}`}
                hover={{
                  basic: [
                    {key: ['miscrevenuedetail', 'amt'], label: 'Amt'},
                    {key: ['miscrevenuedetail', 'amtType'], label: 'Amt Type'},
                    {key: ['miscrevenuedetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'},
                    {key: ['miscrevenuedetail', 'effective'], label: 'Effective'}
                  ],
                  detailed: {single: [{key: ['miscrevenuedetail', 'amtType'], label: 'Amount Type'}],
                             multiple: [{key: ['miscrevenuedetail', 'date'], label: 'Date'}, {key: ['miscrevenuedetail', 'amt'], label: 'Amt'}]}
                }}
              />)}
            )}
          />}
          {props['columns'].includes('T.I.') && <Column style={{minWidth: "160px", maxWidth: "160px"}} className="p-editable-column"
            header="T.I."
            {...field('tidetail', (rowData, props) => { return(
              <CarouselTrigger field="tidetail" rowData={rowData} title="Tenant Improvement"
                persist={persist}
                controls={{
                  basic: {control: Controls.TiBasic, name: 'Basic'},
                  namedItem: {control: Controls.TINamedItem, name: 'Named Item'},
                  detailed: {control: Controls.TIDetailed, name: 'Detailed'}
                }}
                namedItems={{payout, tenantCapital}}
                defaults={{...property['defaultsrentroll']}}
                description={`${_.get(rowData, "tenant")}`}
                componentAtRest={() => {
                  if(!rowData['tidetail'] || rowData['tidetail']?.['state'] === 'basic') { return rowData['tidetail'] ? `${rowData['tidetail']?.['amt'] ? localizeNum(rowData['tidetail']?.['amt'], 2, false) : ''} ${rowData['tidetail']?.['amtType']} ${rowData['tidetail']?.['payout'] ? _.find(payout, {payoutid: rowData['tidetail']['payout']['payoutNamedItemId']})?.['code'] : ''}` : '-' }
                  if(rowData['tidetail']?.['state'] === 'detailed') { return 'Detailed' }
                  if(rowData['tidetail']?.['state'] === 'namedItem') { return `${rowData['tidetail']['tenantCapitalNamedItemId'] ? _.find(tenantCapital, {tenantcapitalid: rowData['tidetail']['tenantCapitalNamedItemId']})?.['code'] : ''}` }
                }}
                hover={{
                  basic: [
                    {key: ['tidetail', 'amt'], label: 'Amt'},
                    {key: ['tidetail', 'amtType'], label: 'Amt Type'},
                    {key: ['tidetail', 'payout', 'payoutNamedItemId'], label: 'Payout'}
                  ],
                  namedItem: [{key: ['tidetail', 'tenantCapitalNamedItemId'], label: 'Named T.I.'}],
                  detailed: {single: [{key: ['tidetail', 'amtType'], label: 'Amount Type'}],
                             multiple: [{key: ['tidetail', 'date'], label: 'Date'}, {key: ['tidetail', 'amt'], label: 'Amt'}]}
                }}
              />)}
            )}
          />}
          {props['columns'].includes('L.C.') && <Column style={{minWidth: "160px", maxWidth: "160px"}} className="p-editable-column"
            header="L.C."
            {...field('lcdetail', (rowData, props) => { return(
              <CarouselTrigger field="lcdetail" rowData={rowData} title="Leasing Commission"
                persist={persist}
                controls={{
                  basic: {control: Controls.LcBasic, name: 'Basic'},
                  namedItem: {control: Controls.LcNamedItem, name: 'Named Item'},
                  detailed: {control: Controls.LcDetailed, name: 'Detailed'}
                }}
                namedItems={{payout, tenantCapital}}
                defaults={{...property['defaultsrentroll']}}
                description={`${_.get(rowData, "tenant")}`}
                componentAtRest={() => {
                  if(!rowData['lcdetail'] || rowData['lcdetail']?.['state'] === 'basic') { return rowData['lcdetail'] ? `${rowData['lcdetail']?.['amt'] ? localizeNum(rowData['lcdetail']?.['amt'], 2, false) : ''} ${rowData['lcdetail']?.['amtType']} ${rowData['lcdetail']?.['payout'] ? _.find(payout, {payoutid: rowData['lcdetail']['payout']['payoutNamedItemId']})?.['code'] : ''}` : '-' }
                  if(rowData['lcdetail']?.['state'] === 'detailed') { return 'Detailed' }
                  if(rowData['lcdetail']?.['state'] === 'namedItem') { return `${rowData['lcdetail']['tenantCapitalNamedItemId'] ? _.find(tenantCapital, {tenantcapitalid: rowData['lcdetail']['tenantCapitalNamedItemId']})?.['code'] : ''}` }
                }}
                hover={{
                  basic: [
                    {key: ['lcdetail', 'amt'], label: 'Amt'},
                    {key: ['lcdetail', 'amtType'], label: 'Amt Type'},
                    {key: ['lcdetail', 'payout', 'payoutNamedItemId'], label: 'Payout'}
                  ],
                  namedItem: [{key: ['lcdetail', 'tenantCapitalNamedItemId'], label: 'Named L.C.'}],
                  detailed: {single: [{key: ['lcdetail', 'amtType'], label: 'Amount Type'}],
                             multiple: [{key: ['lcdetail', 'date'], label: 'Date'}, {key: ['lcdetail', 'amt'], label: 'Amt'}]}
                }}
              />)}
            )}
          />}
          {props['columns'].includes('Other Capital') && <Column style={{minWidth: "160px", maxWidth: "160px"}} className="p-editable-column"
            header="O.C."
            {...field('othercapitaldetail', (rowData, props) => { return(
              <CarouselTrigger field="othercapitaldetail" rowData={rowData} title="Other Capital"
                persist={persist}
                controls={{
                  basic: {control: Controls.OcBasic, name: 'Basic'},
                  namedItem: {control: Controls.OcNamedItem, name: 'Named Item'},
                  detailed: {control: Controls.OcDetailed, name: 'Detailed'}
                }}
                namedItems={{payout, tenantCapital}}
                defaults={{...property['defaultsrentroll']}}
                description={`${_.get(rowData, "tenant")}`}
                componentAtRest={() => {
                  if(!rowData['othercapitaldetail'] || rowData['othercapitaldetail']?.['state'] === 'basic') { return rowData['othercapitaldetail'] ? `${rowData['othercapitaldetail']?.['amt'] ? localizeNum(rowData['othercapitaldetail']?.['amt'], 2, false) : ''} ${rowData['othercapitaldetail']?.['amtType']} ${rowData['othercapitaldetail']?.['payout'] ? _.find(payout, {payoutid: rowData['othercapitaldetail']['payout']['payoutNamedItemId']})?.['code'] : ''}` : '-' }
                  if(rowData['othercapitaldetail']?.['state'] === 'detailed') { return 'Detailed' }
                  if(rowData['othercapitaldetail']?.['state'] === 'namedItem') { return `${rowData['othercapitaldetail']['tenantCapitalNamedItemId'] ? _.find(tenantCapital, {tenantcapitalid: rowData['othercapitaldetail']['tenantCapitalNamedItemId']})?.['code'] : ''}` }
                }}
                hover={{
                  basic: [
                    {key: ['othercapitaldetail', 'amt'], label: 'Amt'},
                    {key: ['othercapitaldetail', 'amtType'], label: 'Amt Type'},
                    {key: ['othercapitaldetail', 'payout', 'payoutNamedItemId'], label: 'Payout'}
                  ],
                  namedItem: [{key: ['othercapitaldetail', 'tenantCapitalNamedItemId'], label: 'Named O.C.'}],
                  detailed: {single: [{key: ['othercapitaldetail', 'amtType'], label: 'Amount Type'}],
                             multiple: [{key: ['othercapitaldetail', 'date'], label: 'Date'}, {key: ['othercapitaldetail', 'amt'], label: 'Amt'}]}
                }}
              />)}
            )}
          />}
          {props['columns'].includes('Percentage Rent') && <Column style={{minWidth: "150px"}} className="p-editable-column"
            header="Sales"
            {...field('salesdetail', (rowData, props) => { return(
              <CarouselTrigger field="salesdetail" rowData={rowData} title="Sales"
                persist={persist}
                disabled={rowData.parentrentrollid}
                controls={{
                  basic: {control: Controls.SalesBasic, name: 'Basic'},
                  detailed: {control: Controls.SalesDetailed, name: 'Detailed'}
                }}
                namedItems={{inflation}}
                defaults={{...property['defaultsrentroll']}}
                componentAtRest={() => {
                  if(!rowData['salesdetail'] || rowData['salesdetail']?.['state'] === 'basic') { return rowData['salesdetail']?.['amt'] ? `${localizeNum(rowData['salesdetail']?.['amt'], 2, false)}${rowData['salesdetail']['amtType'] || ''}` : '-' }
                  else if(rowData['salesdetail']?.['state'] === 'detailed') { return 'Detailed' }
                  else if(rowData['salesdetail']?.['state'] === 'inherit') { return 'Inherit' }
                  else return 'Basic'
                }}
                hover={{
                  basic: [
                    {key: ['salesdetail', 'amt'], label: 'Amt'},
                    {key: ['salesdetail', 'amtType'], label: 'Amt Type'},
                    {key: ['salesdetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'}
                  ],
                  detailed: {
                    single: [{key: ['salesdetail', 'amtType'], label: 'Amount Type'}, {key: ['salesdetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'}],
                    multiple: [{key: ['salesdetail', 'year'], label: 'Year'}, {key: ['salesdetail', 'amt'], label: 'Amt'}]
                  },
                }}
                />)}
              )}
          />}
          {props['columns'].includes('Percentage Rent') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Breakpoint"
            {...field('breakpointdetail', (rowData, props) => { return(
              <CarouselTrigger field="breakpointdetail" rowData={rowData} title="Breakpoint"
                persist={persist}
                controls={{
                  basic: {control: Controls.BreakpointBasic, name: 'Natural'},
                  amount: {control: Controls.BreakpointAmount, name: 'Amount'},
                  detailed: {control: Controls.BreakpointDetailed, name: 'Detailed'}
                }}
                defaults={{...property['defaultsrentroll']}}
                componentAtRest={() => {
                  if(!rowData['breakpointdetail'] || rowData['breakpointdetail']?.['state'] === 'basic') { return rowData['breakpointdetail']?.['breakPoint'] ? 'Natural' : '-' }
                  else if(rowData['breakpointdetail']?.['state'] === 'detailed') { return 'Detailed' }
                  else return 'Amount'
                }}
                hover={{
                  basic: [
                    {key: ['breakpointdetail', 'breakPoint'], label: 'Breakpoint'}
                  ],
                  amount: [
                    {key: ['breakpointdetail', 'amt'], label: 'Amt'},
                    {key: ['breakpointdetail', 'amtType'], label: 'Amt Type'}
                  ],
                  detailed: {
                    single: [{key: ['breakpointdetail', 'amtType'], label: 'Amount Type'}],
                    multiple: [{key: ['breakpointdetail', 'date'], label: 'Date'}, {key: ['breakpointdetail', 'amt'], label: 'Amt'}]
                  }
                }}
                />)}
              )}
          />}
          {props['columns'].includes('Percentage Rent') && <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Overage"
            {...field('overagedetail', (rowData, props) => { return(
              <CarouselTrigger field="overagedetail" rowData={rowData} title="Overage"
                persist={persist}
                controls={{
                  basic: {control: Controls.OverageBasic, name: 'Basic'},
                  detailed: {control: Controls.OverageTiered, name: 'Tiered'}
                }}
                defaults={{...property['defaultsrentroll']}}
                componentAtRest={() => {
                  if(!rowData['overagedetail'] || rowData['overagedetail']?.['state'] === 'basic') { return rowData['overagedetail']?.['percent'] ? `${rowData['overagedetail']?.['percent']}%` : '-' }
                  else return 'Tiered'
                }}
                hover={{
                  basic: [
                    {key: ['overagedetail', 'percent'], label: 'Amt (%)'}
                  ],
                  detailed: {
                    single: [{key: ['overagedetail', 'amtType'], label: 'Amount Type'}],
                    multiple: [{key: ['overagedetail', 'percent'], label: 'Percent'}, {key: ['overagedetail', 'threshold'], label: 'Threshold'}]
                  }
                }}
                />)}
              )}
          />}
          {props['columns'].includes('Renewal Type') && <Column style={{minWidth: "130px", maxWidth: "130px"}} className="p-editable-column"
            header="Renewal Type"
            {...field('renewaltype', (rowData, props) => DropdownField({options: _.map(renewalTypeOptions, (rt) => {return {value: rt, label: rt}}), default: "Weighted", rowData, persist, ...props}))}
            editor={props => <DropdownEdit options={renewalTypeOptions} default="Weighted" persist={persist} {...props} />}
          />}
          {props['columns'].includes('Market Lease') && <Column style={{minWidth: "190px", maxWidth: "190px"}} className="p-editable-column"
            header="Market Lease"
            {...field('marketleaseid', (rowData, props) => DropdownField({options: marketLeaseOptions, default: _.get(property, 'defaultsrentroll.marketLease'), rowData, persist, ...props}))}
            editor={props => <DropdownEdit options={marketLeaseOptions} default={_.get(property, 'defaultsrentroll.marketLease')} persist={persist} {...props} />}
          />}
          {props['columns'].includes('Available Date') && <Column {...field("availabledate.date", (rowData, props) => DateField({...props, 'allowRelative': true}))} header="Available Date" style={{minWidth: "170px", maxWidth: "170px"}}
          editor={props => DateEdit({persist, ...props, 'allowRelative': true})}
          />}
          {props['columns'].includes('Market Override') && <Column style={{minWidth: "140px", maxWidth: "140px"}} className="p-editable-column"
            header="Overrides"
            {...field('overridetype', (rowData, props) => { return(
              <CarouselTrigger field="overridetype" rowData={rowData} title="Overrides"
                persist={persist}
                controls={{
                  single: {control: Controls.Overrides, name: 'Overrides'}
                }}
                componentAtRest={() => {
                  return rowData['overridetype'] && rowData['overridetype'] !== 'None' ? <OverrideIndicator_s>{rowData['overridetype']}</OverrideIndicator_s> : rowData['overridetype'] ? "None" : '-'
                }}
                hover={{
                  injected: OverridesHover
                }}
              />)}
            )}
          />}
          {props['columns'].includes('Notes') && <Column style={{minWidth: "275px", maxWidth: "275px"}} className="p-editable-column"
            header="Notes"
            {...field('notedetail', (rowData, props) =>
              <CarouselTrigger
                field="notedetail"
                rowData={rowData}
                title='Rent Roll'
                persist={persist}
                controls={{single: {control: NotesControls.NotesEdit, name: "Notes"}}}
                componentAtRest={NotesControls.NotesDisplay}
                hover={{
                  injected: NotesHover
                }}
              />
            )}
          />}
        </DataTable> }
        { showReorderConfirmModal &&
          <ConfirmationModal
            show={showReorderConfirmModal}
            title={'Sort Rent Roll'}
            confirmText="Yes"
            cancelText="No"
            message={`Are you sure you want to reorder the Rent Roll by ${sortPayload?.['sortBy']}?`}
            confirmFunc={() => conductSort()}
            cancelFunc={() => setShowReorderConfirmModal(false)}
            toggleShow={() => setShowReorderConfirmModal(false)}
            confirmOnly={false}
            closeOnly={false}
          />
        }
        { carouselData && carouselData['show'] && <CarouselModal code="tenant" data={carouselData} key="carousel-modal" width={1310} /> }
        { deleteConfirmation }
        { optionDeleteConfirmation }
        { duplicateConfirmation }
        { props.data.length ? <TableAutomation tableId={props.tableId} automationActive={!duplicateConfirmation}/> : null }
        <span className="selection-count">{ selected.length ? `Selected Rows: ${selected.length}` : null }</span>
      </RentRollTable_s>
    </div>
  )
}

export default RentRollTable
